import React from 'react';
import Button from '../../../components/ui/Button';

const SalesDataTable = ({ data, onAction, sortConfig, onSort, activeTab, loading = false }) => {
  // Enhanced column configurations for different document types
  const getColumns = () => {
    switch (activeTab) {
      case 'customers':
        return [
          { 
            key: 'customer_name', 
            label: 'Customer Name', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium text-foreground">{item?.customer_name}</div>
                <div className="text-sm text-muted-foreground">{item?.email}</div>
                {item?.company_name && (
                  <div className="text-xs text-muted-foreground">{item?.company_name}</div>
                )}
              </div>
            )
          },
          { 
            key: 'customer_number', 
            label: 'Customer #', 
            sortable: true,
            render: (item) => (
              <span className="font-mono text-sm">{item?.customer_number}</span>
            )
          },
          { 
            key: 'phone', 
            label: 'Phone', 
            sortable: false,
            render: (item) => item?.phone || '-'
          },
          { 
            key: 'total_outstanding', 
            label: 'Outstanding', 
            sortable: true,
            render: (item) => (
              <span className={`font-medium ${parseFloat(item?.total_outstanding || 0) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                ${parseFloat(item?.total_outstanding || 0)?.toLocaleString()}
              </span>
            )
          },
          { 
            key: 'status', 
            label: 'Status', 
            sortable: true,
            render: (item) => (
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                item?.status === 'active' ? 'bg-green-100 text-green-800' :
                item?.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                item?.status === 'suspended'? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {item?.status?.charAt(0)?.toUpperCase() + item?.status?.slice(1)}
              </span>
            )
          },
          { 
            key: 'created_at', 
            label: 'Created', 
            sortable: true,
            render: (item) => new Date(item?.created_at)?.toLocaleDateString()
          }
        ];

      case 'invoices':
        return [
          { 
            key: 'invoice_number', 
            label: 'Invoice #', 
            sortable: true,
            render: (item) => (
              <span className="font-mono text-sm font-medium">{item?.invoice_number}</span>
            )
          },
          { 
            key: 'customer.customer_name', 
            label: 'Customer', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium">{item?.customer?.customer_name}</div>
                <div className="text-sm text-muted-foreground">{item?.customer?.email}</div>
              </div>
            )
          },
          { 
            key: 'total_amount', 
            label: 'Amount', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium">${parseFloat(item?.total_amount || 0)?.toLocaleString()}</div>
                {parseFloat(item?.amount_paid || 0) > 0 && (
                  <div className="text-sm text-green-600">
                    Paid: ${parseFloat(item?.amount_paid)?.toLocaleString()}
                  </div>
                )}
              </div>
            )
          },
          { 
            key: 'issue_date', 
            label: 'Issue Date', 
            sortable: true,
            render: (item) => new Date(item?.issue_date)?.toLocaleDateString()
          },
          { 
            key: 'due_date', 
            label: 'Due Date', 
            sortable: true,
            render: (item) => {
              const dueDate = new Date(item?.due_date);
              const isOverdue = dueDate < new Date() && item?.status !== 'paid';
              return (
                <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                  {dueDate?.toLocaleDateString()}
                </span>
              );
            }
          },
          { 
            key: 'status', 
            label: 'Status', 
            sortable: true,
            render: (item) => (
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                item?.status === 'paid' ? 'bg-green-100 text-green-800' :
                item?.status === 'overdue' ? 'bg-red-100 text-red-800' :
                item?.status === 'partially_paid' ? 'bg-yellow-100 text-yellow-800' :
                item?.status === 'sent'? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {item?.status?.replace('_', ' ')?.toUpperCase()}
              </span>
            )
          }
        ];

      case 'quotes':
        return [
          { 
            key: 'quote_number', 
            label: 'Quote #', 
            sortable: true,
            render: (item) => (
              <span className="font-mono text-sm font-medium">{item?.quote_number}</span>
            )
          },
          { 
            key: 'customer.customer_name', 
            label: 'Customer', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium">{item?.customer?.customer_name}</div>
                <div className="text-sm text-muted-foreground">{item?.customer?.email}</div>
              </div>
            )
          },
          { 
            key: 'total_amount', 
            label: 'Amount', 
            sortable: true,
            render: (item) => (
              <span className="font-medium">${parseFloat(item?.total_amount || 0)?.toLocaleString()}</span>
            )
          },
          { 
            key: 'issue_date', 
            label: 'Issue Date', 
            sortable: true,
            render: (item) => new Date(item?.issue_date)?.toLocaleDateString()
          },
          { 
            key: 'valid_until', 
            label: 'Valid Until', 
            sortable: true,
            render: (item) => {
              const validDate = new Date(item?.valid_until);
              const isExpired = validDate < new Date() && item?.status === 'sent';
              return (
                <span className={isExpired ? 'text-red-600 font-medium' : ''}>
                  {validDate?.toLocaleDateString()}
                </span>
              );
            }
          },
          { 
            key: 'status', 
            label: 'Status', 
            sortable: true,
            render: (item) => (
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                item?.status === 'accepted' ? 'bg-green-100 text-green-800' :
                item?.status === 'converted' ? 'bg-purple-100 text-purple-800' :
                item?.status === 'declined' ? 'bg-red-100 text-red-800' :
                item?.status === 'expired' ? 'bg-orange-100 text-orange-800' :
                item?.status === 'sent'? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {item?.status?.toUpperCase()}
              </span>
            )
          }
        ];

      case 'payments-received':
        return [
          { 
            key: 'payment_reference', 
            label: 'Payment #', 
            sortable: true,
            render: (item) => (
              <span className="font-mono text-sm font-medium">{item?.payment_reference}</span>
            )
          },
          { 
            key: 'customer.customer_name', 
            label: 'Customer', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium">{item?.customer?.customer_name}</div>
                <div className="text-sm text-muted-foreground">{item?.customer?.email}</div>
              </div>
            )
          },
          { 
            key: 'amount_received', 
            label: 'Amount', 
            sortable: true,
            render: (item) => (
              <span className="font-medium text-green-600">
                ${parseFloat(item?.amount_received || 0)?.toLocaleString()}
              </span>
            )
          },
          { 
            key: 'payment_method', 
            label: 'Method', 
            sortable: true,
            render: (item) => (
              <span className="capitalize">{item?.payment_method?.replace('_', ' ')}</span>
            )
          },
          { 
            key: 'payment_date', 
            label: 'Payment Date', 
            sortable: true,
            render: (item) => new Date(item?.payment_date)?.toLocaleDateString()
          },
          { 
            key: 'status', 
            label: 'Status', 
            sortable: true,
            render: (item) => (
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                item?.status === 'cleared' ? 'bg-green-100 text-green-800' :
                item?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                item?.status === 'bounced'? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {item?.status?.toUpperCase()}
              </span>
            )
          }
        ];

      case 'delivery-challans':
        return [
          { 
            key: 'challan_number', 
            label: 'Challan #', 
            sortable: true,
            render: (item) => (
              <span className="font-mono text-sm font-medium">{item?.challan_number}</span>
            )
          },
          { 
            key: 'customer.customer_name', 
            label: 'Customer', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium">{item?.customer?.customer_name}</div>
                <div className="text-sm text-muted-foreground">{item?.customer?.email}</div>
              </div>
            )
          },
          { 
            key: 'transport_mode', 
            label: 'Transport', 
            sortable: true,
            render: (item) => (
              <span className="capitalize">{item?.transport_mode}</span>
            )
          },
          { 
            key: 'vehicle_number', 
            label: 'Vehicle #', 
            sortable: false,
            render: (item) => item?.vehicle_number || '-'
          },
          { 
            key: 'expected_delivery_date', 
            label: 'Expected', 
            sortable: true,
            render: (item) => new Date(item?.expected_delivery_date)?.toLocaleDateString()
          },
          { 
            key: 'status', 
            label: 'Status', 
            sortable: true,
            render: (item) => (
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                item?.status === 'delivered' ? 'bg-green-100 text-green-800' :
                item?.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                item?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                item?.status === 'returned'? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {item?.status?.replace('_', ' ')?.toUpperCase()}
              </span>
            )
          }
        ];

      case 'retainer-invoices':
        return [
          { 
            key: 'retainer_number', 
            label: 'Retainer #', 
            sortable: true,
            render: (item) => (
              <span className="font-mono text-sm font-medium">{item?.retainer_number}</span>
            )
          },
          { 
            key: 'customer.customer_name', 
            label: 'Customer', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium">{item?.customer?.customer_name}</div>
                <div className="text-sm text-muted-foreground">{item?.customer?.email}</div>
              </div>
            )
          },
          { 
            key: 'retainer_amount', 
            label: 'Total Amount', 
            sortable: true,
            render: (item) => (
              <span className="font-medium">${parseFloat(item?.retainer_amount || 0)?.toLocaleString()}</span>
            )
          },
          { 
            key: 'utilized_amount', 
            label: 'Utilized', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium">${parseFloat(item?.utilized_amount || 0)?.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">
                  {item?.utilization_percentage ? `${item?.utilization_percentage?.toFixed(1)}%` : '0%'} used
                </div>
              </div>
            )
          },
          { 
            key: 'start_date', 
            label: 'Start Date', 
            sortable: true,
            render: (item) => new Date(item?.start_date)?.toLocaleDateString()
          },
          { 
            key: 'status', 
            label: 'Status', 
            sortable: true,
            render: (item) => (
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                item?.status === 'active' ? 'bg-green-100 text-green-800' :
                item?.status === 'partially_utilized' ? 'bg-yellow-100 text-yellow-800' :
                item?.status === 'fully_utilized' ? 'bg-blue-100 text-blue-800' :
                item?.status === 'expired'? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {item?.status?.replace('_', ' ')?.toUpperCase()}
              </span>
            )
          }
        ];

      case 'credit-notes':
        return [
          { 
            key: 'credit_note_number', 
            label: 'Credit Note #', 
            sortable: true,
            render: (item) => (
              <span className="font-mono text-sm font-medium">{item?.credit_note_number}</span>
            )
          },
          { 
            key: 'customer.customer_name', 
            label: 'Customer', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium">{item?.customer?.customer_name}</div>
                <div className="text-sm text-muted-foreground">{item?.customer?.email}</div>
              </div>
            )
          },
          { 
            key: 'total_credit_amount', 
            label: 'Credit Amount', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium text-red-600">-${parseFloat(item?.total_credit_amount || 0)?.toLocaleString()}</div>
                {parseFloat(item?.applied_amount || 0) > 0 && (
                  <div className="text-sm text-green-600">
                    Applied: ${parseFloat(item?.applied_amount)?.toLocaleString()}
                  </div>
                )}
              </div>
            )
          },
          { 
            key: 'credit_reason', 
            label: 'Reason', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium capitalize">{item?.credit_reason?.replace('_', ' ')}</div>
                {item?.reason_description && (
                  <div className="text-sm text-muted-foreground">{item?.reason_description}</div>
                )}
              </div>
            )
          },
          { 
            key: 'issue_date', 
            label: 'Issue Date', 
            sortable: true,
            render: (item) => new Date(item?.issue_date)?.toLocaleDateString()
          },
          { 
            key: 'reference_invoice.invoice_number', 
            label: 'Ref. Invoice', 
            sortable: false,
            render: (item) => (
              item?.reference_invoice?.invoice_number ? (
                <span className="font-mono text-sm text-blue-600">{item?.reference_invoice?.invoice_number}</span>
              ) : '-'
            )
          },
          { 
            key: 'status', 
            label: 'Status', 
            sortable: true,
            render: (item) => (
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                item?.status === 'applied' ? 'bg-green-100 text-green-800' :
                item?.status === 'issued' ? 'bg-blue-100 text-blue-800' :
                item?.status === 'cancelled'? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {item?.status?.toUpperCase()}
              </span>
            )
          }
        ];

      case 'payment-links':
        return [
          { 
            key: 'link_reference', 
            label: 'Link Reference', 
            sortable: true,
            render: (item) => (
              <span className="font-mono text-sm font-medium">{item?.link_reference}</span>
            )
          },
          { 
            key: 'customer.customer_name', 
            label: 'Customer', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium">{item?.customer?.customer_name}</div>
                <div className="text-sm text-muted-foreground">{item?.customer?.email}</div>
              </div>
            )
          },
          { 
            key: 'amount', 
            label: 'Amount', 
            sortable: true,
            render: (item) => (
              <span className="font-medium">${parseFloat(item?.amount || 0)?.toLocaleString()}</span>
            )
          },
          { 
            key: 'expires_at', 
            label: 'Expires', 
            sortable: true,
            render: (item) => {
              const expireDate = new Date(item?.expires_at);
              const isExpired = expireDate < new Date() && item?.status !== 'paid';
              return (
                <span className={isExpired ? 'text-red-600 font-medium' : ''}>
                  {expireDate?.toLocaleDateString()}
                </span>
              );
            }
          },
          { 
            key: 'status', 
            label: 'Status', 
            sortable: true,
            render: (item) => (
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                item?.status === 'paid' ? 'bg-green-100 text-green-800' :
                item?.status === 'active' ? 'bg-blue-100 text-blue-800' :
                item?.status === 'expired' ? 'bg-red-100 text-red-800' :
                item?.status === 'cancelled'? 'bg-gray-100 text-gray-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {item?.status?.replace('_', ' ')?.toUpperCase()}
              </span>
            )
          }
        ];

      case 'recurring-invoices':
        return [
          { 
            key: 'recurring_reference', 
            label: 'Recurring #', 
            sortable: true,
            render: (item) => (
              <span className="font-mono text-sm font-medium">{item?.recurring_reference}</span>
            )
          },
          { 
            key: 'customer.customer_name', 
            label: 'Customer', 
            sortable: true,
            render: (item) => (
              <div>
                <div className="font-medium">{item?.customer?.customer_name}</div>
                <div className="text-sm text-muted-foreground">{item?.customer?.email}</div>
              </div>
            )
          },
          { 
            key: 'recurring_amount', 
            label: 'Amount', 
            sortable: true,
            render: (item) => (
              <span className="font-medium">${parseFloat(item?.recurring_amount || 0)?.toLocaleString()}</span>
            )
          },
          { 
            key: 'frequency', 
            label: 'Frequency', 
            sortable: true,
            render: (item) => (
              <span className="capitalize">{item?.frequency}</span>
            )
          },
          { 
            key: 'next_billing_date', 
            label: 'Next Billing', 
            sortable: true,
            render: (item) => new Date(item?.next_billing_date)?.toLocaleDateString()
          },
          { 
            key: 'status', 
            label: 'Status', 
            sortable: true,
            render: (item) => (
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                item?.status === 'active' ? 'bg-green-100 text-green-800' :
                item?.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                item?.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                item?.status === 'cancelled'? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {item?.status?.toUpperCase()}
              </span>
            )
          }
        ];

      default:
        // Generic columns for other types
        return [
          { 
            key: 'id', 
            label: 'ID', 
            sortable: true,
            render: (item) => (
              <span className="font-mono text-sm">{item?.id?.substring(0, 8)}</span>
            )
          },
          { 
            key: 'created_at', 
            label: 'Created', 
            sortable: true,
            render: (item) => new Date(item?.created_at)?.toLocaleDateString()
          }
        ];
    }
  };

  // Get appropriate actions for each tab type
  const getActions = (item) => {
    const baseActions = [
      { key: 'view', label: 'View Details', icon: 'Eye' },
      { key: 'edit', label: 'Edit', icon: 'Edit' }
    ];

    switch (activeTab) {
      case 'quotes':
        if (item?.status === 'sent' || item?.status === 'accepted') {
          baseActions?.push({ key: 'convert', label: 'Convert to Invoice', icon: 'ArrowRight' });
        }
        break;
      case 'invoices':
        if (item?.status === 'draft') {
          baseActions?.push({ key: 'send', label: 'Send', icon: 'Send' });
        }
        // Add Record Payment action for invoices that are not fully paid
        const totalAmount = parseFloat(item?.total_amount || 0);
        const amountPaid = parseFloat(item?.amount_paid || 0);
        const remainingBalance = totalAmount - amountPaid;
        
        if (remainingBalance > 0 && item?.status !== 'cancelled') {
          baseActions?.push({ 
            key: 'record-payment', 
            label: 'Record Payment', 
            icon: 'CreditCard',
            priority: true
          });
        }
        break;
      case 'delivery-challans':
        if (item?.status !== 'delivered') {
          baseActions?.push({ key: 'track', label: 'Track', icon: 'MapPin' });
        }
        break;
    }

    baseActions?.push({ key: 'duplicate', label: 'Duplicate', icon: 'Copy' });
    
    // Add delete action for all items
    baseActions?.push({ key: 'delete', label: 'Delete', icon: 'Trash2', destructive: true });
    
    return baseActions;
  };

  const columns = getColumns();

  const handleSort = (columnKey) => {
    if (onSort) {
      onSort(columnKey);
    }
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) {
      return <svg className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>;
    }

    return sortConfig?.direction === 'asc' ? 
      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4" />
      </svg> :
      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8V4m0 4l4-4m-4 4l-4-4m0 12v-8m0 8l4-4m-4 4l-4-4" />
      </svg>;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-border">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            {/* Header skeleton */}
            <div className="flex space-x-4">
              {[1, 2, 3, 4]?.map((i) => (
                <div key={i} className="h-4 bg-gray-200 rounded w-24"></div>
              ))}
            </div>
            {/* Row skeletons */}
            {[1, 2, 3, 4, 5]?.map((i) => (
              <div key={i} className="flex space-x-4">
                {[1, 2, 3, 4]?.map((j) => (
                  <div key={j} className="h-8 bg-gray-200 rounded w-24"></div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-border shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              {columns?.map((column) => (
                <th
                  key={column?.key}
                  className={`px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider ${
                    column?.sortable ? 'cursor-pointer select-none group hover:bg-muted/70' : ''
                  }`}
                  onClick={column?.sortable ? () => handleSort(column?.key) : undefined}
                >
                  <div className="flex items-center">
                    {column?.label}
                    {column?.sortable && getSortIcon(column?.key)}
                  </div>
                </th>
              ))}
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-border">
            {data?.map((item, index) => (
              <tr key={item?.id || index} className="hover:bg-muted/30 transition-colors">
                {columns?.map((column) => (
                  <td key={column?.key} className="px-6 py-4 whitespace-nowrap text-sm">
                    {column?.render ? column?.render(item) : item?.[column?.key] || '-'}
                  </td>
                ))}
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    {getActions(item)?.map((action) => (
                      <Button
                        key={action?.key}
                        variant="ghost"
                        size="sm"
                        onClick={() => onAction?.(action?.key, item)}
                        iconName={action?.icon}
                        iconPosition="left"
                        className={`${
                          action?.destructive 
                            ? 'text-red-600 hover:text-red-700 hover:bg-red-50' 
                            : action?.priority 
                              ? 'text-green-600 hover:text-green-700 hover:bg-green-50' :'text-muted-foreground hover:text-foreground'
                        }`}
                        title={action?.label}
                      >
                        <span className="sr-only">{action?.label}</span>
                      </Button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesDataTable;